import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./apiClient";

/**
 * Nastavení aplikace — admin API `/admin/api/v1/settings` (ROLE_ADMIN). Instance
 * mode (token settings), kategorie `ui` (nav-visibility toggly) a `http` (obecné,
 * read-only). Admin twin integračního SettingsController.
 */
const BASE = "/admin/api/v1/settings";

export interface InstanceMode {
  currentMode: "SINGLE_INSTANCE" | "MULTI_INSTANCE";
  defaultMode: "SINGLE_INSTANCE" | "MULTI_INSTANCE";
  buildProfile: string;
  description: string;
  cleanupStats?: { deactivatedTokens: number; affectedLicences: number } | null;
}

export interface SettingsCategory {
  category: string;
  config: Record<string, unknown>;
  description: string | null;
}

export function useInstanceMode() {
  return useQuery({ queryKey: ["settings", "instance-mode"], queryFn: () => apiFetch<InstanceMode>(`${BASE}/token/instance-mode`) });
}

export function useSetInstanceMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mode: InstanceMode["currentMode"]) =>
      apiFetch<InstanceMode>(`${BASE}/token/instance-mode`, { method: "PUT", json: { mode } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings", "instance-mode"] });
      qc.invalidateQueries({ queryKey: ["tokens"] });
    },
  });
}

export function useSettingsCategory(category: string) {
  return useQuery({
    queryKey: ["settings", "category", category],
    queryFn: () => apiFetch<SettingsCategory>(`${BASE}/${category}`),
  });
}

export function useUpdateSettingsCategory(category: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (config: Record<string, unknown>) =>
      apiFetch<SettingsCategory>(`${BASE}/${category}`, { method: "PUT", json: { config } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings", "category", category] });
      // nav-visibility gating (permitAll /api/v1/settings/ui/navigation) must refresh
      qc.invalidateQueries({ queryKey: ["settings", "ui", "navigation"] });
    },
  });
}
